import React, { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import FileUpload from '../molecules/FileUpload'
import SelectDropdown from '../molecules/SelectDropdown'
import Button from '../atoms/Button'
import ResultBox from '../organisms/ResultBox'
import Spinner from '../atoms/Spinner'
import { setSourceFile, setTargetFormat, setConverting, setOutputUrl, setError, clearConverterState } from '../store/slices/imageConverterSlice'
import './ImageConverterPage.css'

const ImageConverterPage = ({ tools = [] }) => {
  const dispatch = useDispatch()
  const { sourceFile, targetFormat, outputUrl, isConverting, error } = useSelector(state => state.imageConverter)
  const [previewUrl, setPreviewUrl] = useState('')
  const [lastUpdated, setLastUpdated] = useState(null)

  useEffect(() => {
    setLastUpdated(
      new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    )
  }, [])

  const formatOptions = [
    { value: 'jpg', label: 'JPG' },
    { value: 'png', label: 'PNG' },
    { value: 'webp', label: 'WebP' },
    { value: 'bmp', label: 'BMP' },
    { value: 'gif', label: 'GIF' },
  ]

  const handleFileSelect = (file) => {
    dispatch(setSourceFile(file))
    if (file) {
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
      dispatch(setOutputUrl(null))
    } else {
      setPreviewUrl('')
      dispatch(setOutputUrl(null))
    }
  }

  const handleFormatChange = (e) => {
    dispatch(setTargetFormat(e.target.value))
    dispatch(setOutputUrl(null))
  }

  const handleConvert = async () => {
    if (!sourceFile) return

    dispatch(setConverting(true))
    dispatch(setError(null))

    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()

      img.onload = () => {
        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)

        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob)
            dispatch(setOutputUrl(url))
          }
          dispatch(setConverting(false))
        }, `image/${targetFormat === 'jpg' ? 'jpeg' : targetFormat}`)
      }

      img.onerror = () => {
        dispatch(setError('Failed to load image'))
        dispatch(setConverting(false))
      }

      img.src = previewUrl
    } catch (err) {
      dispatch(setError(err.message))
      dispatch(setConverting(false))
    }
  }

  const handleDownload = () => {
    if (outputUrl) {
      const a = document.createElement('a')
      a.href = outputUrl
      const originalName = sourceFile.name.split('.')[0]
      a.download = `${originalName}.${targetFormat}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }

  const handleClear = () => {
    dispatch(clearConverterState())
    setPreviewUrl('')
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <ToolPageTemplate path="/image-converter/" tools={tools}>
      <div className="image-converter">
        <div className="converter-input-section">
          {!sourceFile ? (
            <FileUpload
              onFileSelect={handleFileSelect}
              accept="image/*"
              maxSize={10 * 1024 * 1024}
            >
              <div className="upload-content">
                <div className="upload-icon"></div>
                <h3>Upload Image</h3>
                <p>Drag and drop an image here or click to browse</p>
                <p className="upload-hint">Supports JPG, PNG, WebP, GIF, BMP (Max 10MB)</p>
              </div>
            </FileUpload>
          ) : (
            <div className="file-selected-info">
              <div className="selected-file-details">
                <h4>Selected Image:</h4>
                <p className="file-name">{sourceFile.name}</p>
                <p className="file-size">
                  {(sourceFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <Button variant="secondary" onClick={handleClear}>
                Choose Different Image
              </Button>
            </div>
          )}

          {sourceFile && (
            <div className="conversion-controls">
              <SelectDropdown
                label="Target Format"
                value={targetFormat}
                onChange={handleFormatChange}
                options={formatOptions}
              />

              <div className="converter-buttons">
                <Button onClick={handleConvert} disabled={isConverting}>
                  {isConverting ? <Spinner size="small" /> : null}
                  Convert Image
                </Button>
                <Button variant="secondary" onClick={handleClear}>
                  Clear
                </Button>
              </div>
            </div>
          )}
        </div>

        {error && (
          <ResultBox title="Error">
            <div className="error-message">{error}</div>
          </ResultBox>
        )}

        {sourceFile && (
          <div className="image-preview-section">
            <div className="preview-grid">
              <div className="preview-item">
                <h4>Original</h4>
                <div className="preview-image">
                  <img src={previewUrl} alt="Original" />
                </div>
                <div className="preview-info">
                  <p><strong>Size:</strong> {formatFileSize(sourceFile.size)}</p>
                  <p><strong>Name:</strong> {sourceFile.name}</p>
                  <p><strong>Type:</strong> {sourceFile.type}</p>
                </div>
              </div>

              {outputUrl && (
                <div className="preview-item">
                  <h4>Converted ({targetFormat.toUpperCase()})</h4>
                  <div className="preview-image">
                    <img src={outputUrl} alt="Converted" />
                  </div>
                  <div className="preview-info">
                    <p><strong>Format:</strong> {targetFormat.toUpperCase()}</p>
                    <Button onClick={handleDownload} size="small">
                      Download
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <section className="tool-guide">
          {/* EXISTING: Kept and enhanced */}
          <div className="guide-section">
            <h2>What Is Image Conversion?</h2>
            <p>Image conversion changes a picture from one file format to another — for example, turning a PNG into a JPG or a JPG into a WebP — without changing what the image shows. Each format stores the same pixels in a different way, so converting lets you trade off file size, image quality, and features like transparency or animation to fit where the image will be used.</p>
            <p>This converter does the whole job inside your browser. You pick a source image and a target format, and it re-encodes the pixels into the new format on your own device. Nothing is uploaded to a server, nothing is stored, and there's no signup — the file goes from your device, through your browser, and back as a download.</p>
            <p>It supports the five formats people convert between most often: <strong>JPG, PNG, WebP, BMP, and GIF</strong>. You can convert in any direction — PNG to JPG, JPG to WebP, WebP to PNG, and so on.</p>
          </div>

          {/* EXISTING: Format comparison kept */}
          <div className="guide-section">
            <h2>Which Image Format Should You Use?</h2>
            <div className="use-cases">
              <div className="use-case">
                <h3>JPG (JPEG)</h3>
                <p>Best for photographs and realistic images with lots of colors. Uses lossy compression for small files, but doesn't support transparency. Ideal when you want a small photo and don't need a see-through background.</p>
              </div>
              <div className="use-case">
                <h3>PNG</h3>
                <p>Best for logos, icons, screenshots, and anything needing a transparent background. Uses lossless compression, so text and sharp edges stay crisp — at the cost of larger files than JPG or WebP.</p>
              </div>
              <div className="use-case">
                <h3>WebP</h3>
                <p>The best all-round choice for the web in 2026. Delivers noticeably smaller files than JPG and PNG at similar quality, and supports both transparency and animation. Backed by all modern browsers.</p>
              </div>
              <div className="use-case">
                <h3>GIF</h3>
                <p>Use for short, simple animations. Limited to 256 colors, so it's a poor choice for photos, but still the most widely supported format for lightweight animated clips.</p>
              </div>
              <div className="use-case">
                <h3>BMP</h3>
                <p>An uncompressed format that keeps every pixel exactly, producing very large files. Occasionally required by older Windows software, but rarely the right choice for the web.</p>
              </div>
            </div>
          </div>

          {/* NEW: Conversion workflow — convert vs resize vs compress */}
          <div className="guide-section">
            <h2>Conversion vs Resizing vs Compression — What's the Difference?</h2>
            <p>These three operations are often confused, but they solve different problems and are best applied in a specific order when optimizing an image for the web:</p>
            <div className="use-cases">
              <div className="use-case">
                <h3>Resizing</h3>
                <p>Changes the pixel dimensions of an image (e.g., 4000×3000 → 1200×900). Reduces file size proportionally and is the single biggest lever for optimization. Always resize first, before converting or compressing — a 4000px image will always be large no matter what format it's in.</p>
              </div>
              <div className="use-case">
                <h3>Compression</h3>
                <p>Reduces file size at the same pixel dimensions by removing redundant or imperceptible data. Our <Link to="/image-compressor/">Image Compressor</Link> handles this. Best applied after resizing, as the last step before publishing.</p>
              </div>
              <div className="use-case">
                <h3>Conversion</h3>
                <p>Changes the file format without changing dimensions. This tool handles conversion. Best applied after resizing, often in combination with compression — for example, converting a resized PNG screenshot to WebP to shrink it further.</p>
              </div>
            </div>
            <p>Recommended workflow for web images: <strong>resize → convert to WebP → compress at 80–85%</strong>. This combination typically achieves 85–95% size reduction from the original while keeping the image visually identical to the source on any modern browser.</p>
          </div>

          {/* NEW: Real size numbers */}
          <div className="guide-section">
            <h2>Typical File Size Changes by Conversion</h2>
            <p>Format choice has an enormous impact on file size. These are typical results users see for the same image at the same resolution:</p>
            <div className="use-cases">
              <div className="use-case">
                <h3>PNG → WebP (best savings)</h3>
                <p><strong>Before:</strong> 1.5–3 MB · <strong>After:</strong> 200–400 KB · <strong>Savings:</strong> 80–90%. WebP is the single largest size win available for most images, especially screenshots and photos.</p>
              </div>
              <div className="use-case">
                <h3>JPG → WebP</h3>
                <p><strong>Before:</strong> 500 KB · <strong>After:</strong> 250–350 KB · <strong>Savings:</strong> 30–50%. Smaller than PNG-to-WebP because JPG starts smaller, but still a solid improvement for web use.</p>
              </div>
              <div className="use-case">
                <h3>PNG → JPG</h3>
                <p><strong>Before:</strong> 2 MB · <strong>After:</strong> 300–500 KB · <strong>Savings:</strong> 75–85%. Works well for photographs, but loses transparency. Only choose this if the PNG has no transparent areas.</p>
              </div>
              <div className="use-case">
                <h3>WebP → JPG (backward compatibility)</h3>
                <p><strong>Before:</strong> 300 KB · <strong>After:</strong> 400–500 KB · <strong>Size increase:</strong> 30–60%. Converting WebP back to JPG makes files larger — only do it if the target system doesn't support WebP (older email clients, some CMS platforms, legacy software).</p>
              </div>
            </div>
          </div>

          {/* EXISTING: How to Use (kept, tightened) */}
          <div className="guide-section">
            <h2>How to Use the Image Converter</h2>
            <div className="guide-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Upload your image</h3>
                  <p>Click the upload area or drag and drop your file. The tool accepts JPG, PNG, WebP, BMP, and GIF, and checks the file automatically before converting.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Choose the target format</h3>
                  <p>Pick the format you want from the dropdown. If you're not sure, WebP is the safest choice for web use and JPG for sharing photos.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Convert and compare</h3>
                  <p>Click convert and the file is processed instantly in your browser. A preview appears next to the original so you can check the result before saving.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Download the result</h3>
                  <p>Happy with it? Click download to save the converted image to your device in the new format.</p>
                </div>
              </div>
            </div>
          </div>

          {/* NEW: Common conversion mistakes */}
          <div className="guide-section">
            <h2>Common Image Conversion Mistakes</h2>
            <p><strong>Converting the same image repeatedly.</strong> Each lossy conversion (to JPG, lossy WebP) throws away a little quality. Converting JPG → PNG → JPG → WebP loses data at every lossy step. Always convert from the original source file, or export once and preserve that master copy.</p>
            <p><strong>Converting a PNG with transparency to JPG.</strong> JPG doesn't support transparency. Any transparent areas in a PNG (or WebP with alpha) become black or white — usually ugly. If transparency matters, convert to PNG or WebP instead.</p>
            <p><strong>Choosing BMP or TIFF for web use.</strong> These formats preserve every pixel exactly but produce very large files. They're occasionally required for print workflows or legacy Windows software, but never for the web. WebP, JPG, or PNG will always be the better choice for any browser destination.</p>
            <p><strong>Skipping WebP because "some browsers don't support it."</strong> This was true until roughly 2020. Today, WebP is supported in every current version of Chrome, Safari, Firefox, and Edge — over 96% of browsers in use. The right pattern is to serve WebP as the default and provide a JPG or PNG fallback only if your stack requires it.</p>
          </div>

          {/* EXISTING: FAQ expanded from 6 to 10 */}
          <div className="guide-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              <div className="faq-item">
                <h3>How do I convert a PNG to a JPG?</h3>
                <p>Upload your PNG, choose JPG as the target format, then convert and download. Keep in mind that JPG has no transparency, so any transparent areas in the PNG will be filled with a solid background. Convert to JPG when you want a smaller file and don't need a see-through background.</p>
              </div>
              <div className="faq-item">
                <h3>Will converting to JPG lose transparency?</h3>
                <p>Yes. JPG doesn't support transparency, so transparent areas from a PNG or WebP source become a solid background when converted. If you need to keep transparency, convert to PNG or WebP instead.</p>
              </div>
              <div className="faq-item">
                <h3>Does converting an image reduce its quality?</h3>
                <p>It depends on the target format. Converting to a lossless format like PNG keeps the quality intact. Converting to a lossy format like JPG can introduce slight compression, and converting an already-compressed JPG repeatedly will degrade it over time. For the best result, always convert from your highest-quality original.</p>
              </div>
              <div className="faq-item">
                <h3>Which format is best for my website?</h3>
                <p>WebP is generally the best choice in 2026 — smaller files than JPG or PNG at comparable quality, with support for transparency and animation, and it works in all modern browsers. Use PNG only when you specifically need lossless quality or maximum compatibility.</p>
              </div>
              <div className="faq-item">
                <h3>Are my images uploaded to a server?</h3>
                <p>No. Conversion runs entirely in your browser using client-side JavaScript, so your images never leave your device and nothing is stored or tracked. That also means there's no hard file-size limit — though very large files take a little longer since your device does the work.</p>
              </div>
              <div className="faq-item">
                <h3>Can I convert an animated GIF?</h3>
                <p>You can convert a GIF to a static image format like PNG or JPG, which will capture a single frame. To keep animation, WebP is the better modern target since it supports animated sequences at much smaller file sizes than GIF.</p>
              </div>
              <div className="faq-item">
                <h3>What's the difference between image conversion and image compression?</h3>
                <p>Conversion changes the file format (PNG to JPG, JPG to WebP) without changing the pixel dimensions. Compression reduces file size at the same format and dimensions by removing data. They're complementary — for the best web performance, resize, convert to WebP, and compress. See our <Link to="/image-compressor/">Image Compressor</Link> for the compression step.</p>
              </div>
              <div className="faq-item">
                <h3>Can I convert HEIC photos from my iPhone?</h3>
                <p>Not directly in this tool — browser support for HEIC decoding is still inconsistent, so the source file must be in a format your browser can already display (JPG, PNG, WebP, GIF, BMP). On iPhone, go to Settings → Camera → Formats → "Most Compatible" to capture future photos as JPG, or use Apple's Photos app to export existing HEIC files as JPG before converting here.</p>
              </div>
              <div className="faq-item">
                <h3>Does converting to WebP actually save space?</h3>
                <p>Yes — significantly. WebP's lossy compression typically produces files 25–35% smaller than an equivalent JPG, and its lossless mode beats PNG by a similar margin. For a typical PNG screenshot, converting to WebP often cuts the file size by 80% or more. For most websites, switching to WebP is one of the single largest page-weight reductions available.</p>
              </div>
              <div className="faq-item">
                <h3>Will the converted image work everywhere?</h3>
                <p>JPG and PNG are universally supported. WebP is supported in every current browser (Chrome, Safari, Firefox, Edge) and covers roughly 96% of browsers in use today — enough for direct serving without a fallback in almost all cases. BMP is mainly a Windows-native format and works everywhere but produces very large files. GIF works everywhere but only for simple graphics and animations.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Related Tools</h2>
            <div className="related-tools">
              <Link to="/image-compressor/" className="related-tool">
                <div className="tool-icon">Image Compressor</div>
                <div className="tool-description">Reduce image file size without losing visible quality</div>
              </Link>
              <Link to="/qr-generator/" className="related-tool">
                <div className="tool-icon">QR Code Generator</div>
                <div className="tool-description">Generate QR codes for URLs, Wi-Fi, text, and more</div>
              </Link>
              <Link to="/word-counter/" className="related-tool">
                <div className="tool-icon">Word Counter</div>
                <div className="tool-description">Count words, characters, and analyze your text</div>
              </Link>
              <Link to="/yt-thumbnail/" className="related-tool">
                <div className="tool-icon">YouTube Thumbnail Downloader</div>
                <div className="tool-description">Download and convert YouTube video thumbnails</div>
              </Link>
              <Link to="/blog/image-format-guide-jpg-vs-png-vs-webp/" className="related-tool">
                <div className="tool-icon">JPG vs PNG vs WebP Guide</div>
                <div className="tool-description">Which image format to use and when</div>
              </Link>
            </div>
          </div>
        </section>

        <div className="last-updated">
          <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>
        </div>
      </div>
    </ToolPageTemplate>
  )
}

export default ImageConverterPage